// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import i18n from '@/i18n';
import { IErrorMessage } from '@protolabs/component-library/src/interfaces/customValidation';
import { ComponentPublicInstance, computed } from 'vue';

const emailRegex =
    /^[a-z0-9!#$%&'*+=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?$/;

export const max = (maxLength: number) => ({
    validationFunction: (value: string | undefined | null) => {
        if (!value) {
            return true;
        }
        return value.length <= maxLength;
    },
    arguments: [maxLength],
    errorMessageText: computed(() => i18n.global.t('validation.messages.max', ['', maxLength])),
});

export const min = (minLength: number) => ({
    validationFunction: (value: string | undefined | null) => {
        if (!value) {
            return true;
        }
        return value.length >= minLength;
    },
    arguments: [minLength],
    errorMessageText: computed(() => i18n.global.t('validation.messages.min', ['', minLength])),
});

export const required = {
    validationFunction: (value: string | undefined | null) => {
        return !!value;
    },
    errorMessageText: computed(() => i18n.global.t('validation.messages.required')),
};

export const emailValidator = {
    validationFunction: (value: string) => {
        return emailRegex.test(value.toLowerCase());
    },
    errorMessageText: computed(() => i18n.global.t('validation.messages.email')),
};

export const formValidator = (component: ComponentPublicInstance, refs: Record<string, string> | Array<string>) => {
    let refsToCheck: string[] = [];

    if (Array.isArray(refs)) {
        refsToCheck = refs;
    } else {
        refsToCheck = Object.keys(refs);
    }

    let results: IErrorMessage[] = [];

    for (const ref of refsToCheck) {
        const checkRef: any = component.$refs[ref];
        if (checkRef && checkRef.validate) {
            const result = checkRef.validate();
            if (result.length > 0) {
                results = results.concat(result);
            }
        }
        continue;
    }

    return results;
};

export type { IErrorMessage };

export default {
    max,
    min,
    required,
    emailValidator,
    formValidator,
};
